import { responsiveFontSizes } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { createTheme as createMuiTheme, ThemeOptions } from '@mui/material/styles';
import { lighten, darken } from '@mui/material/styles';
import colors from './colors';
import { shadows } from './shadows';
import typography from './typography';
import { components } from './overrides';

export const THEMES = {
  LIGHT: 'LIGHT',
  DARK: 'DARK',
};

const baseOptions: ThemeOptions = {
};

const themesOptions = [
  {
    name: THEMES.LIGHT,
    palette: {
      primary: {
        light: lighten(colors.brand.main, 0.2),
        main: colors.brand.main,
        dark: darken(colors.brand.main, 0.2),
        background: lighten(colors.brand.main, 0.95),
        contrastText: '#fff',
      },
      secondary: {
        light: lighten(colors.grey[1000], 0.2),
        main: colors.grey[1000],
        dark: darken(colors.grey[1000], 0.2),
        contrastText: '#fff',
      },
      tertiary: {
        light: lighten(colors.grey[600], 0.2),
        main: colors.grey[800],
        dark: darken(colors.grey[600], 0.2),
        contrastText: '#fff',
      },
      text: {
        primary: colors.grey[1000],
        secondary: colors.grey[800],
      },
      background: {
        default: '#fafafa',
        grey: '#ECECEC',
        lightGray: '#F9F9F9',
      },
      action: {
        disabled: colors.grey[400],
        disabledBackground: colors.grey[300],
        hover: lighten(colors.brand.main, 0.95),
      },
      error: {
        light: lighten(colors['state-error'], 0.2),
        main: colors['state-error'],
        dark: colors['state-error-dark'],
        background: lighten(colors['state-error'], 0.8),
        contrastText: '#fff',
      },
      warning: {
        light: lighten(colors['state-warning'], 0.3),
        main: colors['state-warning'],
        dark: colors['state-warning-dark'],
        contrastText: colors.grey[900],
      },
      success: {
        light: lighten(colors['state-success'], 0.2),
        main: colors['state-success'],
        dark: darken(colors['state-success'], 0.15),
        background: lighten(colors['state-success'], 0.9),
        contrastText: '#fff',
      },
      white: "#FFFFFF",
      grey: colors.grey,
      badge: colors.badge,
      brand: colors.brand,
      blueprint: colors.blueprint
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920
      },
    }
  },
  {
    name: THEMES.DARK,
    palette: {
      mode: 'dark',
      action: {
        active: 'rgba(255, 255, 255, 0.54)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)',
      },
      background: {
        default: '#282C34',
        dark: '#1c2025',
        paper: '#282C34',
      },
      primary: {
        main: '#8a85ff',
      },
      secondary: {
        main: '#8a85ff',
      },
      text: {
        primary: '#e6e5e8',
        secondary: '#adb0bb',
      },
    }
  },
];

type ThemeConfig = {
  componentsOverride?: object;
  typographyOverride?: object;
  theme?: string;
  direction?: string;
  responsiveFontSizes?: boolean;
};

export const createTheme = (config: ThemeConfig = { 
  componentsOverride: {}, 
  typographyOverride: {}, 
  theme: THEMES.LIGHT, 
  direction: 'ltr', 
  responsiveFontSizes: false 
}) => {
  let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  const finalThemeOptions = { 
    ...themeOptions,
    components: { ...components, ...config.componentsOverride },
    typography: { ...typography, ...config.typographyOverride },
    direction: config.direction,
    shadows
  };

  let theme = createMuiTheme(
    deepmerge(baseOptions, finalThemeOptions)
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};

export const getTheme = (theme = THEMES.LIGHT) => {
  const themeOptions = themesOptions.find((t) => t.name === theme);
  return themeOptions ? themeOptions : null;
}

export const getPalette = (theme = THEMES.LIGHT) => {
  return getTheme(theme)?.palette;
}

export default {
  getTheme,
  getPalette,
  THEMES,
  createTheme,
  colors,
};